import { Locale } from "@omni/types/enums/locale.enum";
import { attempt } from "@omni/utils/function.utils";
import { getLanguageTag } from "@omni/utils/locale.utils";
import {
    differenceInCalendarMonths,
    differenceInDays,
    differenceInHours,
    differenceInMonths,
    differenceInWeeks,
    differenceInYears,
    isAfter,
    subDays,
    subHours,
    subMonths
} from "date-fns";

import { Config } from "@/utils/config.utils";

export const formatNumber = (
    value: number,
    locale?: Locale,
    options?: Omit<Intl.NumberFormatOptions, "useGrouping">
) => {
    return attempt(() => {
        const languageTag = getLanguageTag(locale ?? Config.getLocale());

        const group = Math.trunc(value * 10) / 10 >= 10_000;

        return group
            ? new Intl.NumberFormat(languageTag, {
                  useGrouping: true,
                  ...options
              })
                  .formatToParts(value)
                  .map((part) => (part.type === "group" ? " " : part.value))
                  .join("")
            : new Intl.NumberFormat(languageTag, {
                  useGrouping: false,
                  ...options
              }).format(value);
    }, "");
};

export const formatRelativeDate = (date: Date, locale?: Locale) => {
    return attempt(() => {
        const languageTag = getLanguageTag(locale ?? Config.getLocale());

        const rtf = new Intl.RelativeTimeFormat(languageTag, {
            localeMatcher: "best fit",
            numeric: "always",
            style: "long"
        });

        const slices: {
            condition: (date: Date) => boolean;
            getDifference: (date: Date, dateToCompare: Date) => number;
            unit: Intl.RelativeTimeFormatUnit;
        }[] = [
            {
                condition: (date) => isAfter(date, subHours(new Date(), 24)),
                getDifference: differenceInHours,
                unit: "hours"
            },
            {
                condition: (date) => isAfter(date, subDays(new Date(), 7)),
                getDifference: differenceInDays,
                unit: "days"
            },
            {
                condition: (date) =>
                    isAfter(date, subMonths(new Date(), 12)) &&
                    Math.abs(differenceInMonths(date, new Date())) > 0,
                getDifference: differenceInCalendarMonths,
                unit: "months"
            },
            {
                condition: (date) => {
                    const diff = differenceInWeeks(date, new Date());
                    return Math.abs(diff) < 5;
                },
                getDifference: differenceInWeeks,
                unit: "weeks"
            },
            {
                condition: () => true,
                getDifference: differenceInYears,
                unit: "years"
            }
        ];

        if (Math.abs(differenceInHours(date, new Date())) < 1) {
            return "Zojuist";
        }

        for (const slice of slices) {
            if (slice.condition(date)) {
                return rtf.format(
                    slice.getDifference(date, new Date()),
                    slice.unit
                );
            }
        }

        return null;
    }, null);
};
